import { useEffect, useRef, useCallback } from 'react';
import Phaser from 'phaser';
import AvatarScene from './AvatarScene';
import './PhaserAvatar.css';

// Internal Phaser canvas is 240×360; CSS scales it to `size` px wide.
// Character (128×160) is centred inside, giving 56px horizontal and 100px vertical breathing room.
// Taller canvas gives a portrait frame — character sits in the centre with background above/below.
const INTERNAL_W = 240;
const INTERNAL_H = 360;

export default function PhaserAvatar({ avatarConfig = {}, size = 128, animated = true, onSceneReady }) {
  const wrapRef   = useRef(null);
  const gameRef   = useRef(null);
  const sceneRef  = useRef(null);
  const configRef = useRef(avatarConfig);

  // Keep latest config accessible without re-running the mount effect
  configRef.current = avatarConfig;

  const mountGame = useCallback(() => {
    if (!wrapRef.current || gameRef.current) return;

    const game = new Phaser.Game({
      type:         Phaser.CANVAS,
      width:        INTERNAL_W,
      height:       INTERNAL_H,
      backgroundColor: 'transparent',
      transparent:  true,
      parent:       wrapRef.current,
      pixelArt:     true,
      roundPixels:  true,
      render: { antialias: false, pixelArt: true },
      scene:        [],
      scale: {
        mode:   Phaser.Scale.NONE,
        width:  INTERNAL_W,
        height: INTERNAL_H,
      },
      audio: { noAudio: true },
    });

    gameRef.current = game;

    game.events.once('ready', () => {
      game.scene.add('AvatarScene', AvatarScene);
      game.scene.start('AvatarScene', {
        config:   configRef.current,
        animated,
      });
      // Small delay to let the scene finish create()
      setTimeout(() => {
        const scene = game.scene.getScene('AvatarScene');
        sceneRef.current = scene;
        if (onSceneReady) onSceneReady(scene);
      }, 60);
    });
  }, [animated, onSceneReady]);

  // Mount on first render
  useEffect(() => {
    mountGame();
    return () => {
      if (gameRef.current) {
        gameRef.current.destroy(true);
        gameRef.current = null;
        sceneRef.current = null;
      }
    };
  }, []); // intentionally mount-only

  // Push config changes into the live scene whenever avatarConfig prop changes
  useEffect(() => {
    const scene = sceneRef.current;
    if (scene && typeof scene.updateConfig === 'function') {
      scene.updateConfig(avatarConfig);
    }
  });

  // CSS scales the 160×200 canvas to `size` px wide (preserving aspect ratio)
  const scale = size / INTERNAL_W;

  return (
    <div
      className="phaser-avatar-outer"
      style={{ width: size, height: Math.round(INTERNAL_H * scale) }}
    >
      <div
        ref={wrapRef}
        className="phaser-avatar-inner"
        style={{
          width:     INTERNAL_W,
          height:    INTERNAL_H,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      />
    </div>
  );
}
